import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { createPriceValidationMessage } from '@/domain/pricing/utils/validation';
import { createStyles } from '@/contexts/theme-context';
import {
  formatLocalePrice,
  stripCommas,
} from '@/domain/pricing/utils/pricing-utils';
import { formatPriceDisplayFromMicros } from '@/utils/price-editing';
import { Icons } from '@usepepper/sprout-kit-ui';
import {
  OrderEntryInputCellShortcutCallbacks,
  useOrderEntryCellShortcuts,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-order-entry-cell-shortcuts';
import { PeakPriceProtectionIcon } from '@/domain/pricing/components/peak-price-protection-icon';
import { Platform, TextInput, View } from 'react-native';
import { PRICE_INPUT_WIDTH } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-constants';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { PriceEditType } from '@/types';
import { PriceOverrideInfo } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceOverrideInfoDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/price-override-info';
import {
  QuickAction,
  QuickActionMenu,
} from '@/experiences/sales-portal/components/quick-action-menu';
import { SalesPortalKeyboardScope } from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCellKeyboardNavigation } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-cell-keyboard-navigation';
import { useCellQuickActions } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-cell-quick-actions';
import { useDebouncedCallback } from 'use-debounce';
import { usePriceEditorState } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-editor-state';
import { usePriceEditUnitDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-price-edit-unit-display';
import { usePricingCellIcon } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-pricing-cell-icon';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { useRafThrottle } from '@experiences/hooks/use-raf-throttle';
import { useTemporaryAdjustmentQuickAction } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-temporary-adjustment-quick-action';

/** Only allow digits, commas, and a single decimal point (e.g. "12", "1,234.5", ".5") */
const PRICE_INPUT_REGEX = /^[\d,]*\.?\d*$/;

/** Strips non-numeric characters (currency symbols, spaces) from a display string */
const CURRENCY_STRIP_REGEX = /[^0-9.,]/g;

const REVERT_ICON_SIZE = 16;

const INPUT_HEIGHT = 32;
const INPUT_MIN_WIDTH = 48;
const VALIDATION_DEBOUNCE_MS = 300;

// Estimated badge sizing at paragraph-xs typography. Derives paddingRight and
// marginRight from unitSuffix length without runtime layout measurement.
// Derived from paragraph-xs font metrics and spaceXsmall token.
const UNIT_BADGE_MAX_CHARS = 3;
const UNIT_BADGE_CHAR_WIDTH = 7;
const UNIT_BADGE_PADDING_OVERHEAD = 10; // paddingHorizontal (8) + border (2)
const UNIT_BADGE_MAX_WIDTH =
  UNIT_BADGE_PADDING_OVERHEAD + UNIT_BADGE_CHAR_WIDTH * UNIT_BADGE_MAX_CHARS;
const getUnitBadgeHalfWidth = (suffix: string) =>
  Math.ceil(
    (UNIT_BADGE_PADDING_OVERHEAD +
      UNIT_BADGE_CHAR_WIDTH * Math.min(suffix.length, UNIT_BADGE_MAX_CHARS)) /
      2,
  );

interface OrderEntryTablePriceCellProps {
  canSetManualOverrideExpiration?: boolean;
  onSave: (editor: PriceEditor, variantPackUUID: string) => void;
  onValidationError: (message: string) => void;
  priceDisplayString: string;
  priceOverrideInfo?: PriceOverrideInfo;
  /**
   * Rendered index (sorted position) passed from extended cell context.
   */
  rowIndex: number;
  shortcutCallbacks?: OrderEntryInputCellShortcutCallbacks;
  unitCount: number;
  unitPriceInfo?: string;
  unitSuffix?: string;
  variantPackUUID: string;
}

export const OrderEntryTablePriceCell =
  React.memo<OrderEntryTablePriceCellProps>(
    ({
      canSetManualOverrideExpiration,
      onSave,
      onValidationError,
      priceDisplayString,
      priceOverrideInfo,
      rowIndex,
      shortcutCallbacks,
      unitCount,
      unitPriceInfo,
      unitSuffix,
      variantPackUUID,
    }) => {
      const [styles, theme] = useStyles();
      const throttle = useRafThrottle();

      const editor = usePricingStore.getState().editors.get(variantPackUUID);
      const editorState = usePriceEditorState(variantPackUUID);
      const {
        isPerUnit,
        secondaryPriceDisplay,
        showSecondaryPrice,
        unitBadgeLabel,
      } = usePriceEditUnitDisplay({
        editor,
        editorState,
        priceDisplayString,
        unitCount,
        unitPriceInfo,
        unitSuffix,
      });

      // canWrite=true because the screen-level isPriceEditingEnabled already
      // verified WRITE permission — this checks editor-level flags only (cost data, VP settings)
      const isEditable = useMemo(
        () => editor?.isPriceEditingEnabled(true) ?? false,
        [editor],
      );
      const isPeakPriceProtected = useMemo(
        () => editor?.isPeakPriceProtectionInEffect() ?? false,
        [editor],
      );

      const isSaving = editor?.isSaveInProgress() ?? false;
      const disabled = !editor || !editorState || !isEditable || isSaving;

      const handleShortcutKeyPress =
        useOrderEntryCellShortcuts(shortcutCallbacks);

      // On native iOS, editable={false} prevents the TextInput from receiving
      // Tab focus via external keyboard. Keep it editable so Tab navigation
      // works, and use showSoftInputOnFocus to suppress the keyboard instead.
      const nativeEditable = Platform.OS !== 'web' || !disabled;

      const {
        handleBlurFocusCleanup,
        handleFocus,
        handleKeyPress: handleNavigationKeyPress,
        inputRef,
        isCellFocused,
      } = useCellKeyboardNavigation({
        columnId: ColumnId.Price,
        onPreKeyPress: handleShortcutKeyPress,
        rowIndex,
      });

      const defaultPriceText =
        editor?.getDefaultPriceDisplayText() ||
        priceDisplayString.replace(CURRENCY_STRIP_REGEX, '');

      const hasOverride = priceOverrideInfo?.activeOverride !== undefined;

      const temporaryAdjustmentAction = useTemporaryAdjustmentQuickAction({
        editor,
        hasActiveOverride: hasOverride,
        isDirty: editorState?.isDirty ?? false,
        isEnabled: !!canSetManualOverrideExpiration && !disabled,
      });

      const quickActions = useMemo<QuickAction[]>(() => {
        if (disabled) {
          return [];
        }
        return [
          ...(temporaryAdjustmentAction ? [temporaryAdjustmentAction] : []),
          {
            detail: defaultPriceText ? `$${defaultPriceText}` : undefined,
            icon: (
              <Icons.RotateCcw
                color={theme.sk.color.text.primary}
                size={REVERT_ICON_SIZE}
              />
            ),
            key: 'revert',
            label: i18n.t('sales_portal.order_entry.quick_actions.revert'),
            onSelect: () => {
              editor.revertToDefaultPrice();
            },
          },
        ];
      }, [
        defaultPriceText,
        disabled,
        editor,
        temporaryAdjustmentAction,
        theme.sk.color.text.primary,
      ]);

      const {
        handleKeyPress,
        handleQuickActionSelect,
        isEditing,
        isQuickActionsOpen,
        isQuickActionsOpenRef,
        saveAndReset,
        setIsEditing,
      } = useCellQuickActions({
        disabled,
        editor,
        handleStandardKeyPress: handleNavigationKeyPress,
        inputRef,
        onSave,
        quickActions,
        throttle,
        variantPackUUID,
      });

      // Debounced inline validation — shows error icon while typing
      const [validationMessage, setValidationMessage] = useState<string | null>(
        null,
      );

      // Derive: clear validation when editor resets (e.g. after clearEdits)
      const activeValidationMessage = editorState?.isDirty
        ? validationMessage
        : null;

      const debouncedValidate = useDebouncedCallback(() => {
        if (!editor) {
          return;
        }

        const result = editor.validate(editor.getState());

        if (!result.valid && result.reason) {
          setValidationMessage(
            createPriceValidationMessage(result.reason, result.bound),
          );
        } else {
          setValidationMessage(null);
        }
      }, VALIDATION_DEBOUNCE_MS);

      const handleChangeText = useCallback(
        (text: string) => {
          if (disabled) {
            return;
          }
          if (text && !PRICE_INPUT_REGEX.test(text)) {
            return;
          }
          setIsEditing(true);
          // Strip commas so PriceEditor receives a plain numeric string
          const numericText = stripCommas(text);
          editor?.updatePrice(numericText);
          debouncedValidate();
        },
        [debouncedValidate, disabled, editor, setIsEditing],
      );

      const handleBlur = useCallback(async () => {
        if (isQuickActionsOpenRef.current) {
          return;
        }
        handleBlurFocusCleanup();

        if (!editor) {
          setIsEditing(false);
          return;
        }

        const currentState = editor.getState();

        if (currentState.isDirty) {
          // Check validation before save — revert + toast on failure
          const validation = editor.validate(currentState);

          if (!validation.valid && validation.reason) {
            const msg = createPriceValidationMessage(
              validation.reason,
              validation.bound,
            );
            editor.clearEdits();

            setValidationMessage(null);

            if (msg) {
              onValidationError(msg);
            }

            setIsEditing(false);
            return;
          }

          await onSave(editor, variantPackUUID);
        }

        setValidationMessage(null);
        setIsEditing(false);
      }, [
        editor,
        handleBlurFocusCleanup,
        isQuickActionsOpenRef,
        onSave,
        onValidationError,
        setIsEditing,
        variantPackUUID,
      ]);

      // When actively editing or dirty, show raw editor text (allows clearing).
      // Otherwise, fall back to the portion-type-aware base price from the editor,
      // or the static pack price when no editor exists.
      const isUserEditing = isEditing || editorState?.isDirty;
      const priceFallbackText =
        formatPriceDisplayFromMicros(
          editor?.getCurrentPriceInMicros() ??
            editor?.getBasePriceInMicros() ??
            null,
        ) || priceDisplayString.replace(CURRENCY_STRIP_REGEX, '');
      const rawDisplayText = isUserEditing
        ? (editorState?.priceDisplayText ?? '')
        : editorState?.priceDisplayText || priceFallbackText;
      // Add comma separators to the displayed value for readability
      const priceInputDisplayValue = formatLocalePrice(rawDisplayText);

      const icon = usePricingCellIcon({
        activeValidationMessage,
        disabled,
        isCellFocused,
        isPeakPriceProtected,
        isSaving,
        quickActionsLength: quickActions.length,
        theme,
      });
      const fallbackIcon =
        isPeakPriceProtected && !isCellFocused ? (
          <PeakPriceProtectionIcon />
        ) : null;

      const badgeHalfWidth = unitBadgeLabel
        ? getUnitBadgeHalfWidth(unitBadgeLabel)
        : 0;

      const priceInput = (
        <View style={unitBadgeLabel ? styles.unitBadgeWrapper : undefined}>
          <View
            style={[
              styles.priceInput,
              isCellFocused && styles.priceInputFocused,
              disabled && styles.priceInputDisabled,
              !!unitBadgeLabel && { paddingRight: badgeHalfWidth },
            ]}
          >
            {icon ? <View style={styles.iconSlot}>{icon}</View> : fallbackIcon}
            <TextInput
              editable={nativeEditable}
              keyboardType="decimal-pad"
              onBlur={handleBlur}
              onChangeText={handleChangeText}
              onFocus={handleFocus}
              onKeyPress={handleKeyPress}
              ref={inputRef}
              selectTextOnFocus
              showSoftInputOnFocus={!disabled}
              style={[styles.inputText, disabled && styles.inputTextDisabled]}
              value={priceInputDisplayValue}
            />
          </View>
          {unitBadgeLabel ? (
            <View
              style={[
                styles.unitBadge,
                disabled && styles.unitBadgeDisabled,
                { marginLeft: -badgeHalfWidth },
              ]}
            >
              <Typography
                color={theme.sk.color.text.tertiary}
                numberOfLines={1}
                variant="paragraph-xs"
              >
                {unitBadgeLabel}
              </Typography>
            </View>
          ) : null}
        </View>
      );

      return (
        <View
          style={[styles.cellContainer, hasOverride && styles.overrideCell]}
        >
          <View style={[styles.row, !!unitBadgeLabel && styles.rowWithBadge]}>
            <QuickActionMenu
              actions={quickActions}
              isOpen={isQuickActionsOpen}
              keyboardScope={SalesPortalKeyboardScope.QUICK_ACTION_MENU}
              onActionSelect={handleQuickActionSelect}
              onClose={saveAndReset}
              trigger={priceInput}
            />
            {secondaryPriceDisplay ? (
              <View style={styles.unitPriceContainer}>
                <Typography
                  color={theme.sk.color.text.tertiary}
                  numberOfLines={1}
                  variant="paragraph-sm"
                >
                  {secondaryPriceDisplay}
                </Typography>
              </View>
            ) : null}
          </View>
          {hasOverride && priceOverrideInfo ? (
            <PriceOverrideInfoDisplay
              defaultSecondaryValue={
                showSecondaryPrice
                  ? isPerUnit
                    ? priceOverrideInfo.defaultPackPriceDisplay
                    : priceOverrideInfo.defaultUnitPriceDisplay
                  : undefined
              }
              defaultValue={
                isPerUnit
                  ? (priceOverrideInfo.defaultUnitPriceDisplay ?? '')
                  : priceOverrideInfo.defaultPackPriceDisplay
              }
              editedByName={priceOverrideInfo.editedByName}
              expiryDateDisplay={priceOverrideInfo.expiryDateDisplay}
              lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_price"
              showOverrideIndicator={
                priceOverrideInfo.activeOverride === PriceEditType.Price
              }
              showTemporaryIndicator={
                !!canSetManualOverrideExpiration &&
                priceOverrideInfo.isTemporaryOverride
              }
            />
          ) : null}
        </View>
      );
    },
  );

const useStyles = createStyles((theme) => ({
  cellContainer: {
    flex: 1,
  },
  iconSlot: {
    alignItems: 'center',
    flexShrink: 0,
    justifyContent: 'center',
    width: theme.sk.spacing.spaceMedium,
  },
  inputText: {
    ...theme.sk.typography.input.md,
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: theme.sk.color.text.primary,
    flex: 1,
    minWidth: 0,
    padding: 0,
    textAlign: 'right',
  },
  inputTextDisabled: {
    color: theme.sk.color.text.disabled,
  },
  overrideCell: {
    minHeight: theme.sk.spacing.space3xlarge,
  },
  priceInput: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    height: INPUT_HEIGHT,
    minWidth: INPUT_MIN_WIDTH,
    overflow: 'hidden',
    paddingLeft: theme.sk.spacing.spaceXsmall,
    paddingRight: theme.sk.spacing.spaceSMedium,
    width: PRICE_INPUT_WIDTH,
  },
  priceInputDisabled: {
    backgroundColor: theme.sk.color.bg.primary.disabled,
  },
  priceInputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  rowWithBadge: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  unitBadge: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 1,
    justifyContent: 'center',
    maxWidth: UNIT_BADGE_MAX_WIDTH,
    overflow: 'hidden',
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
  unitBadgeDisabled: {
    backgroundColor: theme.sk.color.bg.primary.disabled,
  },
  unitBadgeWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  unitPriceContainer: {
    flexShrink: 0,
  },
}));
